import React, { useEffect, useState } from "react";
import { paymentMethods } from "../helperFunctions";
import { makeCardPayment } from "./card";
import { makeKlarnaPayment } from "./klarna";
import { useStripe } from "@stripe/react-stripe-js";
import { createStripePayment } from "./common";
import UserContext from "../../contexts/UserContext";
import LayoutContext from "../../contexts/LayoutContext";
import { useRef } from "react";

const paymentStatus = {
    SUCCESS: "SUCCESS",
    FAILED: "FAILED"
};
const makePayment = async ({
    type = "",
    cardElement = null,
    checkoutPaymentData = {},
    savedPaymentMethodType = "",
    paymentIntent = {},
    paymentPopupData = {},
    stripe,
    paymentRequest
}) => {
    if (type === paymentMethods.CARD) {
        const data = await makeCardPayment({
            cardElement,
            checkoutPaymentData,
            savedPaymentMethodType,
            paymentIntent,
            paymentPopupData,
            stripe
        });

        return data;
    } else if (type === paymentMethods.KLARNA_PAY) {
        const data = await makeKlarnaPayment({
            paymentIntent,
            stripe,
            paymentPopupData
        });

        return data;
    } else if ((type === paymentMethods.APPLE_PAY || type === paymentMethods.GOOGLE_PAY) && paymentRequest) {
        paymentRequest?.show?.();
    }
};

const paymentPopUptValidation = ({ type = "", stripeCardData = {}, paymentPopupData = {} }) => {
    const data = {
        isValidPaymentInfo: false
    };
    if (type === paymentMethods.CARD) {
        const { isCardCvv, isCardExpData, isCardNumber, isComplete } = stripeCardData;
        data.isValidPaymentInfo = paymentPopupData?.nameOnCard?.length > 0 && isCardCvv && isCardExpData && isCardNumber && isComplete;
    } else if (type === paymentMethods.KLARNA_PAY) {
        data.isValidPaymentInfo = true;
    } else if (type === paymentMethods.APPLE_PAY || type === paymentMethods.GOOGLE_PAY) {
        data.isValidPaymentInfo = true;
    }

    return data;
};

const handlePaymentMethods = async ({
    type = "",
    cardElement = null,
    checkoutPaymentData = {},
    savedPaymentMethodType = "",
    paymentIntent = {},
    paymentPopupData = {},
    stripe,
    paymentRequest
}) => {
    if (type === paymentMethods.CARD) {
        const data = await makePayment({
            type: paymentMethods.CARD,
            cardElement,
            paymentIntent,
            stripe,
            savedPaymentMethodType,
            checkoutPaymentData,
            paymentPopupData
        });
        return data;
    } else if (type === paymentMethods.KLARNA_PAY) {
        const data = await makePayment({
            type: paymentMethods.KLARNA_PAY,
            paymentIntent,
            stripe,
            paymentPopupData
        });
        return data;
    } else if (type === paymentMethods.APPLE_PAY || type === paymentMethods.GOOGLE_PAY) {
        const data = await makePayment({
            type,
            paymentIntent,
            stripe,
            paymentPopupData,
            paymentRequest
        });
        return data;
    }
};

const useSocialPayment = ({ onError = () => {}, onSuccess = () => {}, cartData }) => {
    const stripe = useStripe();
    const { paymentRequest } = useShowSocialPayment();
    const { checkoutPaymentData, BOTagAccountID, userData } = React.useContext(UserContext);
    const { paymentPopupData } = React.useContext(LayoutContext);

    const selectedPaymentTypeRef = useRef(paymentPopupData);

    useEffect(() => {
        selectedPaymentTypeRef.current = paymentPopupData;
    }, [paymentPopupData]);

    useEffect(() => {
        if (!paymentRequest) {
            return;
        }
        paymentRequest?.on?.("paymentmethod", async event => {
            try {
                const { data: response } = await createStripePayment({
                    cartData,
                    checkoutPaymentData,
                    paymentPopupData: selectedPaymentTypeRef.current,
                    selectedPaymentType: paymentMethods.GOOGLE_PAY,
                    userData
                });

                const client_secret = await response?.paymentIntent?.client_secret;

                if (client_secret) {
                    const result = await stripe.confirmCardPayment(client_secret, {
                        payment_method: event.paymentMethod.id
                    });
                    const payload = {
                        res: response,
                        savedPaymentMethodType: null,
                        data: {
                            isSuccessFull: true,
                            paymentMethod: event.paymentMethod?.id
                        },
                        cardElement: null,
                        BOTagAccountID,
                        selectedPaymentType: paymentMethods.GOOGLE_PAY
                    };

                    if (result.error) {
                        event.complete("fail");
                        onError?.();
                    } else if (result.paymentIntent.status === "requires_action") {
                        const confirmResult = await stripe.confirmCardPayment(client_secret);
                        if (confirmResult?.paymentIntent?.status === "succeeded") {
                            event.complete("success");
                            onSuccess?.(payload);
                        } else {
                            event.complete("fail");
                            onError?.();
                        }
                    } else if (result.paymentIntent.status === "succeeded") {
                        event.complete("success");

                        onSuccess?.(payload);
                    } else {
                        event.complete("fail");
                        onError?.();
                    }
                } else {
                    event.complete("fail");
                    onError?.();
                }
            } catch (error) {
                event.complete("fail");
                onError?.();
            }
        });
        paymentRequest.on("cancel", () => {
            onError?.();
        });
    }, [stripe, paymentRequest]);
    return { paymentRequest };
};
const useShowSocialPayment = () => {
    const stripe = useStripe();
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [checkPaymentMethods, setCheckPaymentMethods] = useState({
        applePay: false,
        googlePay: false,
        link: false
    });

    useEffect(() => {
        if (!stripe) {
            return;
        }
        const pr = stripe.paymentRequest({
            country: "NO",
            currency: "nok",
            requestPayerName: true,
            requestPayerEmail: true,
            total: {
                label: "Demo total",
                amount: 1000
            }
        });
        pr.canMakePayment().then(result => {
            if (result) {
                setCheckPaymentMethods({
                    applePay: result.applePay,
                    googlePay: result.googlePay,
                    link: result.link
                });
                setPaymentRequest(pr);
            }
        });
    }, [stripe]);
    return { paymentRequest, checkPaymentMethods };
};

export { paymentStatus, makePayment, paymentPopUptValidation, handlePaymentMethods, useSocialPayment, useShowSocialPayment };
